/**
 * The icon and tint one audit module wears.
 *
 * Split from `lib/auditAction.ts` on purpose: that file is the rules and is
 * tested without React, this one is a lookup table that pulls in icon
 * components. Both screens that read the ledger - Settings -> Logs and a
 * client's Logs tab - draw from this single table, so a module can never be
 * green on one screen and orange on the other.
 *
 * The colours are borrowed from `ClientTimeline.tsx` deliberately, module for
 * module: the two lists sit one tab apart and describe the same objects, and an
 * invoice that is emerald on one and violet on the other would read as two
 * different kinds of thing.
 */
import {
  AtSign, Banknote, Boxes, Building2, Calculator, FileMinus2, FileSignature, FileText,
  FolderKanban, KeyRound, LifeBuoy, ListTodo, Mail, MonitorSmartphone, Megaphone,
  ReceiptText, Settings2, ShieldCheck, StickyNote, UploadCloud, UserRound, Wallet, Workflow,
  type LucideIcon,
} from "lucide-react";
import { auditModule } from "../../../lib/auditAction";

export interface AuditMeta {
  icon: LucideIcon;
  /** Tailwind classes for the round badge: a tinted background and a text tone. */
  dot: string;
}

/** The fallback, for a module nobody has given a face yet. */
const UNKNOWN: AuditMeta = { icon: StickyNote, dot: "bg-slate-400/15 text-slate-500" };

const MODULE_META: Record<string, AuditMeta> = {
  client: { icon: Building2, dot: "bg-slate-400/15 text-slate-500" },
  contact: { icon: UserRound, dot: "bg-slate-400/15 text-slate-500" },
  lead: { icon: UserRound, dot: "bg-amber-500/15 text-amber-600" },
  person: { icon: UserRound, dot: "bg-slate-400/15 text-slate-500" },
  duplicate: { icon: Building2, dot: "bg-amber-500/15 text-amber-600" },
  portal: { icon: MonitorSmartphone, dot: "bg-emerald-500/15 text-emerald-600" },
  mail: { icon: Mail, dot: "bg-sky-500/15 text-sky-500" },
  marketing: { icon: Megaphone, dot: "bg-fuchsia-500/15 text-fuchsia-500" },
  invoice: { icon: ReceiptText, dot: "bg-emerald-500/15 text-emerald-600" },
  estimate: { icon: Calculator, dot: "bg-indigo-500/15 text-indigo-500" },
  proposal: { icon: FileText, dot: "bg-violet-500/15 text-violet-500" },
  payment: { icon: Banknote, dot: "bg-green-500/15 text-green-600" },
  credit_note: { icon: FileMinus2, dot: "bg-rose-500/15 text-rose-500" },
  expense: { icon: Wallet, dot: "bg-amber-500/15 text-amber-600" },
  item: { icon: Boxes, dot: "bg-fuchsia-500/15 text-fuchsia-500" },
  sales: { icon: FileText, dot: "bg-violet-500/15 text-violet-500" },
  project: { icon: FolderKanban, dot: "bg-blue-500/15 text-blue-500" },
  task: { icon: ListTodo, dot: "bg-cyan-500/15 text-cyan-600" },
  milestone: { icon: ListTodo, dot: "bg-cyan-500/15 text-cyan-600" },
  flowmap: { icon: Workflow, dot: "bg-violet-500/15 text-violet-500" },
  contract: { icon: FileSignature, dot: "bg-teal-500/15 text-teal-600" },
  contract_template: { icon: FileSignature, dot: "bg-teal-500/15 text-teal-600" },
  ticket: { icon: LifeBuoy, dot: "bg-orange-500/15 text-orange-500" },
  canned_reply: { icon: AtSign, dot: "bg-orange-500/15 text-orange-500" },
  vault: { icon: KeyRound, dot: "bg-red-500/15 text-red-500" },
  import: { icon: UploadCloud, dot: "bg-blue-500/15 text-blue-500" },
  billing: { icon: Banknote, dot: "bg-green-500/15 text-green-600" },
  company: { icon: Settings2, dot: "bg-slate-400/15 text-slate-500" },
  numbering: { icon: Settings2, dot: "bg-slate-400/15 text-slate-500" },
  settings: { icon: Settings2, dot: "bg-slate-400/15 text-slate-500" },
  security: { icon: ShieldCheck, dot: "bg-red-500/15 text-red-500" },
};

export function auditMeta(action: string): AuditMeta {
  return MODULE_META[auditModule(action)] ?? UNKNOWN;
}

/** Whether this module has a face of its own, for tests and for the filter row. */
export function hasAuditMeta(module: string): boolean {
  return module in MODULE_META;
}
